import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { 
  Send, 
  Plus, 
  Play, 
  Pause, 
  Trash2, 
  Edit,
  Calendar,
  Users,
  Search,
  Clock,
  Mail,
  MessageSquare
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/toast";
import { 
  getCampaigns, 
  createCampaign, 
  deleteCampaign, 
  startCampaign, 
  pauseCampaign,
  scheduleCampaign
} from "@/lib/marketing-api";

const getStatusLabels = (t) => ({
  draft: { label: t('marketingFull.draft'), color: "bg-muted text-muted-foreground" },
  active: { label: t('marketingFull.active'), color: "bg-green-500/20 text-green-400" },
  paused: { label: t('marketingFull.paused'), color: "bg-yellow-500/20 text-yellow-400" },
  completed: { label: t('marketingFull.completed'), color: "bg-blue-500/20 text-blue-400" },
  scheduled: { label: t('marketingFull.scheduledFor'), color: "bg-purple-500/20 text-purple-400" }
});

const getChannelLabels = (t) => ({
  telegram: { label: t('marketingFull.telegram'), icon: Send },
  email: { label: t('marketingFull.email'), icon: Mail },
  sms: { label: t('marketingFull.sms'), icon: MessageSquare }
});

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const { t } = useTranslation();
  
  const statusLabels = getStatusLabels(t);
  const channelLabels = getChannelLabels(t);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    message_template: "",
    status: "draft",
    channel: "telegram"
  });
  const [scheduleData, setScheduleData] = useState({
    scheduled_at: "",
    channel: "telegram"
  });

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      const response = await getCampaigns();
      setCampaigns(response.data?.data || response.data || []);
    } catch (error) {
      toast.error(t('errors.serverError'));
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await createCampaign(formData);
      toast.success(t('success.created'));
      setIsDialogOpen(false);
      setFormData({ name: "", description: "", message_template: "", status: "draft", channel: "telegram" });
      loadCampaigns();
    } catch (error) {
      toast.error(t('marketingFull.errorCreatingCampaign'));
    }
  };

  const handleStart = async (id) => {
    try {
      await startCampaign(id);
      toast.success(t('marketingFull.campaignStarted'));
      loadCampaigns();
    } catch (error) {
      toast.error(t('marketingFull.errorStartingCampaign'));
    }
  };

  const handlePause = async (id) => {
    try {
      await pauseCampaign(id);
      toast.success(t('marketingFull.campaignPaused'));
      loadCampaigns();
    } catch (error) {
      toast.error(t('marketingFull.errorPausingCampaign'));
    }
  };

  const handleSchedule = async (e) => {
    e.preventDefault();
    if (!selectedCampaign) return;
    
    try {
      await scheduleCampaign(selectedCampaign.id, {
        scheduled_at: new Date(scheduleData.scheduled_at).toISOString(),
        channel: scheduleData.channel
      });
      toast.success(t('success.updated'));
      setIsScheduleDialogOpen(false);
      setSelectedCampaign(null);
      setScheduleData({ scheduled_at: "", channel: "telegram" });
      loadCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.serverError'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('marketingFull.confirmDeleteCampaign'))) return;
    try {
      await deleteCampaign(id);
      toast.success(t('success.deleted'));
      loadCampaigns();
    } catch (error) {
      toast.error(t('errors.serverError'));
    }
  };

  const openScheduleDialog = (campaign) => {
    setSelectedCampaign(campaign);
    setScheduleData({ 
      scheduled_at: "", 
      channel: campaign.channel || "telegram" 
    });
    setIsScheduleDialogOpen(true);
  };

  const filteredCampaigns = Array.isArray(campaigns) ? campaigns.filter(campaign => {
    const matchesSearch = (campaign.name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || campaign.status === statusFilter;
    return matchesSearch && matchesStatus;
  }) : [];
  
  const isRTL = document.documentElement.dir === 'rtl';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="campaigns-page">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-black text-foreground">{t('marketingFull.campaignsTitle')}</h1>
          <p className="text-muted-foreground mt-1">{t('marketingFull.campaignsSubtitle')}</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold px-6 py-2 shadow-lg shadow-emerald-500/30 text-base" size="lg" data-testid="create-campaign-btn">
              <Plus className="w-5 h-5 me-2" />
              + {t('marketingFull.newCampaign')}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]" dir={isRTL ? "rtl" : "ltr"}>
            <DialogHeader>
              <DialogTitle>{t('marketingFull.createNewCampaign')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="form-group">
                <Label htmlFor="name">{t('marketingFull.campaignName')}</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('marketingFull.campaignName')}
                  required
                  data-testid="campaign-name-input"
                />
              </div>
              <div className="form-group">
                <Label htmlFor="channel">{t('marketingFull.sendChannel')}</Label>
                <Select 
                  value={formData.channel} 
                  onValueChange={(value) => setFormData({ ...formData, channel: value })}
                >
                  <SelectTrigger data-testid="campaign-channel-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="telegram">
                      <span className="flex items-center gap-2">
                        <Send className="w-4 h-4" />
                        {t('marketingFull.telegram')}
                      </span>
                    </SelectItem>
                    <SelectItem value="email">
                      <span className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        {t('marketingFull.email')}
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="form-group">
                <Label htmlFor="description">{t('marketingFull.campaignDescription')}</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('marketingFull.campaignDescPlaceholder')}
                  data-testid="campaign-description-input"
                />
              </div>
              <div className="form-group">
                <Label htmlFor="message_template">{t('marketingFull.messageTemplate')}</Label>
                <Textarea
                  id="message_template"
                  value={formData.message_template}
                  onChange={(e) => setFormData({ ...formData, message_template: e.target.value })}
                  placeholder={t('marketingFull.messageTemplatePlaceholder')}
                  rows={4}
                  required
                  data-testid="campaign-message-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {t('marketingFull.messageTemplateNote')}
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" data-testid="submit-campaign-btn">
                  {t('marketingFull.createCampaign')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Schedule Dialog */}
      <Dialog open={isScheduleDialogOpen} onOpenChange={setIsScheduleDialogOpen}>
        <DialogContent className="sm:max-w-[400px]" dir={isRTL ? "rtl" : "ltr"}>
          <DialogHeader>
            <DialogTitle>{t('marketingFull.scheduleCampaign')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSchedule} className="space-y-4 mt-4">
            <div className="form-group">
              <Label htmlFor="scheduled_at">{t('marketingFull.scheduleDateTime')}</Label>
              <Input
                id="scheduled_at"
                type="datetime-local"
                value={scheduleData.scheduled_at}
                onChange={(e) => setScheduleData({ ...scheduleData, scheduled_at: e.target.value })}
                required
                data-testid="schedule-datetime-input"
              />
            </div>
            <div className="form-group">
              <Label htmlFor="schedule_channel">{t('marketingFull.sendChannel')}</Label>
              <Select 
                value={scheduleData.channel} 
                onValueChange={(value) => setScheduleData({ ...scheduleData, channel: value })}
              >
                <SelectTrigger data-testid="schedule-channel-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="telegram">{t('marketingFull.telegram')}</SelectItem>
                  <SelectItem value="email">{t('marketingFull.email')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsScheduleDialogOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" data-testid="confirm-schedule-btn">
                <Clock className="w-4 h-4 me-2" />
                {t('marketingFull.schedule')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('marketingFull.searchCampaigns')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pe-10"
                data-testid="search-campaigns-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[180px]" data-testid="status-filter">
                <SelectValue placeholder={t('marketingFull.status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('marketingFull.allStatuses')}</SelectItem>
                <SelectItem value="draft">{t('marketingFull.draft')}</SelectItem>
                <SelectItem value="active">{t('marketingFull.active')}</SelectItem>
                <SelectItem value="scheduled">{t('marketingFull.scheduled')}</SelectItem>
                <SelectItem value="paused">{t('marketingFull.paused')}</SelectItem>
                <SelectItem value="completed">{t('marketingFull.completed')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Campaigns Grid */}
      {filteredCampaigns.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="empty-state">
              <Send className="empty-state-icon" />
              <h3 className="empty-state-title">{t('marketingFull.noCampaigns')}</h3>
              <p className="empty-state-description">{t('marketingFull.createFirstCampaign')}</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCampaigns.map((campaign) => {
            const status = statusLabels[campaign.status] || statusLabels.draft;
            const channel = channelLabels[campaign.channel] || channelLabels.telegram;
            const ChannelIcon = channel.icon;
            
            return (
              <Card key={campaign.id} className="card-hover" data-testid={`campaign-card-${campaign.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{campaign.name}</CardTitle>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge className={status.color}>
                          {status.label}
                        </Badge>
                        <Badge variant="outline" className="flex items-center gap-1">
                          <ChannelIcon className="w-3 h-3" />
                          {channel.label}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {campaign.description || t('marketingFull.noDescription')}
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Users className="w-4 h-4" />
                      <span>{t('marketingFull.sentMessages')}: </span>
                      <span className="font-numbers">{campaign.sent_count || 0}</span>
                      {campaign.failed_count > 0 && (
                        <span className="text-red-400 font-numbers">
                          ({t('marketingFull.failed')}: {campaign.failed_count})
                        </span>
                      )}
                    </div>
                    {campaign.scheduled_at && (
                      <div className="flex items-center gap-2 text-purple-400">
                        <Clock className="w-4 h-4" />
                        <span>{t('marketingFull.scheduled')}: </span>
                        <span className="font-numbers">
                          {new Date(campaign.scheduled_at).toLocaleString(isRTL ? "ar-SA" : "en-US")}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="w-4 h-4" />
                      <span>{t('marketingFull.createdAt')}: </span>
                      <span className="font-numbers">
                        {new Date(campaign.created_at).toLocaleDateString(isRTL ? "ar-SA" : "en-US")}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4 pt-4 border-t border-border flex-wrap">
                    {(campaign.status === "draft" || campaign.status === "paused") && (
                      <>
                        <Button 
                          size="sm" 
                          onClick={() => handleStart(campaign.id)}
                          data-testid={`start-campaign-${campaign.id}`}
                        >
                          <Play className="w-4 h-4 me-1" />
                          {t('marketingFull.startCampaign')}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => openScheduleDialog(campaign)}
                          data-testid={`schedule-campaign-${campaign.id}`}
                        >
                          <Clock className="w-4 h-4 me-1" />
                          {t('marketingFull.schedule')}
                        </Button>
                      </>
                    )}
                    {campaign.status === "active" && (
                      <Button 
                        size="sm" 
                        variant="secondary"
                        onClick={() => handlePause(campaign.id)}
                        data-testid={`pause-campaign-${campaign.id}`}
                      >
                        <Pause className="w-4 h-4 me-1" />
                        {t('marketingFull.pauseCampaign')}
                      </Button>
                    )}
                    {campaign.status === "scheduled" && (
                      <Badge variant="outline" className="text-purple-400">
                        <Clock className="w-3 h-3 me-1" />
                        {t('marketingFull.waiting')}
                      </Badge>
                    )}
                    <Button size="sm" variant="outline">
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="destructive"
                      onClick={() => handleDelete(campaign.id)}
                      data-testid={`delete-campaign-${campaign.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
